import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { selectRecordsData } from "./ballotSelectRecordType";

const initialState: selectRecordsData = {
  accreditationBodyOptions: [],
  primaryCommitteOptions: [],
  ballotTypeOptions: [],
  ballotSubTypeOptions: [],
  ballotIdOptions: [],
  ballotLevelOptions: [],
  recordTypeOptions: [],
  recordSubTypeOptions: [],
  positionAppliedOptions: [],
  standardNameOptions: [],
  ballotStatusOptions: [],
  selectedRecordValues: {
    accreditationBodySelect: [],
    primaryCommitteSelect: [],
    ballotTypeSelect: [],
    ballotSubTypeSelect: [],
    ballotIdSelect: [],
    ballotLevelSelect: [],
    recordTypeSelect: [],
    recordSubTypeSelect: [],
    positionAppliedSelect: [],
    standardNameSelect: [],
  },
  selectedTableRecords: [],
  preservedSelectedTableRecords: [],
  recordTableData: [],
  recordTableFilterData: [],
  postBallotRecord: false,
  stepCount: 0,
  isEdit: false,
  countChange: false,
  recordCount: 0,
  loading: false
};

const selectRecords = createSlice({
  name: 'ballotSelectRecords',
  initialState,
  reducers: {
    initialRecordValues: (state, action: PayloadAction<Record<string, any>>) => {
      const formatOptions = (options: any[], labelKey: string, valueKey: string, idKey: string, nameKey?: string) => options.map((item: any) => ({
          label: item[labelKey],
          value: item[valueKey],
          id: item[idKey],
          name: nameKey && item[nameKey]
        }));
      const formatPrimaryOptions = (options: any[], labelKey: string, valueKey: string, idKey: string, count = '', secondaryLabelKey?: string) =>
        options.map((item: any) => ({
          label: secondaryLabelKey ? `${item[labelKey]} (${item[secondaryLabelKey]})` : item[labelKey],
          value: item[valueKey],
          id: item[idKey],
          count: item[count]
        }));
      switch (action.payload.label) {
        case 'accreditationType':
          state.accreditationBodyOptions = formatOptions(action.payload.options.Collection, 'DisplayName', 'Name', 'Id');
          break;
        case 'primarycommittee':
          state.primaryCommitteOptions = formatPrimaryOptions(action.payload.options, 'Name', 'Name', 'Id', 'MemberCount', 'FileNumber');
          break;
        case 'ballottype':
          state.ballotTypeOptions = formatOptions(action.payload.options.Collection, 'DisplayName', 'Name', 'Id');
          break;
        case 'ballotsubtype':
          state.ballotSubTypeOptions = formatOptions(action.payload.options.Collection, 'DisplayName', 'Name', 'Id');
          break;
        case 'ballotid':
          state.ballotIdOptions = formatOptions(action.payload.options.Collection, 'BallotNumber', 'BallotNumber', 'BallotId');
          break;
        case 'ballotlevel':
          state.ballotLevelOptions = formatOptions(action.payload.options.Collection, 'DisplayName', 'Name', 'Id', "Name");
          break;
        case 'recordtype':
          state.recordTypeOptions = formatOptions(action.payload.options.Collection, 'DisplayName', 'TypeName', 'Id');
          break;
        case 'recordsubtype':
          state.recordSubTypeOptions = formatOptions(
            action.payload.options.Collection
              .filter((item: any) => item.TypeDisplayName),
            'TypeDisplayName',
            'Type',
            'Id'
          );
          break;
        case 'positions':
          state.positionAppliedOptions = formatOptions(action.payload.options, 'displayName', 'positionName', 'id');
          break;
        case 'ballotStatus':
          state.ballotStatusOptions = formatOptions(action.payload.options.Collection, 'Name', 'DisplayName', 'Id');
          break;
        case 'standardname':
          state.standardNameOptions = action.payload.options.Collection.map((item: any) => ({
            label: item.Designation ? `${item.Designation} - ${item.StandardName}` : item.StandardName,
            value: item.StandardName,
            id: item.StandardId,
            standardNumber: item.Designation,
          }));
          break;
        default:
          break;
      }
    },
    setSelectedTableRecords: (state, action: PayloadAction<any[]>) => {
      state.selectedTableRecords = action.payload;
    },
    selectedRecordValues: (state, action: PayloadAction<Record<string, any>>) => {
      const optionMappings: any = {
        'accreditationBodyValue': state.accreditationBodyOptions,
        'primaryCommitteeValue': state.primaryCommitteOptions,
        'ballotTypeValue': state.ballotTypeOptions,
        'ballotSubTypeValue': state.ballotSubTypeOptions,
        'ballotIdValue': state.ballotIdOptions,
        'ballotLevelValue': state.ballotLevelOptions,
        'recordTypeValue': state.recordTypeOptions,
        'recordSubTypeValue': state.recordSubTypeOptions,
        'positionAppliedValue': state.positionAppliedOptions,
        'standardNameValue': state.standardNameOptions,
      };

      const optionFieldMapping: Record<string, string> = {
        'accreditationBodyValue': 'accreditationBodySelect',
        'primaryCommitteeValue': 'primaryCommitteSelect',
        'ballotTypeValue': 'ballotTypeSelect',
        'ballotSubTypeValue': 'ballotSubTypeSelect',
        'ballotIdValue': 'ballotIdSelect',
        'ballotLevelValue': 'ballotLevelSelect',
        'recordTypeValue': 'recordTypeSelect',
        'recordSubTypeValue': 'recordSubTypeSelect',
        'positionAppliedValue': 'positionAppliedSelect',
        'standardNameValue': 'standardNameSelect',
      };
      const selectedField = optionFieldMapping[action.payload.label];
      if (action.payload.label === 'positionAppliedValue' || action.payload.label === 'standardNameValue') {
        const targets = Array.isArray(action.payload.target) ? action.payload.target : [action.payload.target];
        const selectedOptions = targets
          .map((value: any) =>
            optionMappings[action.payload.label]?.find((option: any) => option.value === value)
          )
          .filter((option: any) => option);
        (state.selectedRecordValues as any)[selectedField] = selectedOptions.length >= 0
          ? selectedOptions
          : [];

        return;
      }
      const selectedOption = optionMappings[action.payload.label]?.find(
        (option: any) => option.value === action.payload.target
      );
      if (selectedOption) {
        const selectedField = optionFieldMapping[action.payload.label];
        (state.selectedRecordValues as any)[selectedField] = selectedOption;
      }
      if (action.payload.target === "") {
        const placeholderOption = { value: "", label: "Select an option" };
        const selectedField = optionFieldMapping[action.payload.label];
        (state.selectedRecordValues as any)[selectedField] = placeholderOption;
        return;
      }
    },
    tableRecordSelectionHandler: (state, action: PayloadAction<Record<string, any>>) => {
      const newRecord = action.payload.rowData;

      // ✅ NORMALIZE ID (same identity for both mirrored records)
      const normalizeId = (item: any) =>
        item.ballotRecordId || item.id;

      const newKey = normalizeId(newRecord);

      const index = state.selectedTableRecords.findIndex(
        (obj: any) => normalizeId(obj) === newKey
      );

      if (index === -1) {
        // ✅ ADD
        state.selectedTableRecords.push(newRecord);

        const existsInPreserved = state.preservedSelectedTableRecords.some(
          (rec: any) => normalizeId(rec) === newKey
        );

        if (!existsInPreserved) {
          state.preservedSelectedTableRecords.push(newRecord);
        }

      } else {
        // ✅ REMOVE (THIS FIXES YOUR BUG 🔥)
        state.selectedTableRecords = state.selectedTableRecords.filter(
          (rec: any) => normalizeId(rec) !== newKey
        );
      }
    },
    addRecordTableData: (state, action: PayloadAction<Record<string, any>>) => {
      state.recordTableData = action.payload.rowTableData;
      // Only clear selection if you are not in recirculation mode and you really want to reset
      if (action.payload.shouldResetSelection) {
        state.selectedTableRecords = [];
        state.preservedSelectedTableRecords = [];
      }
      if (action.payload.isRecirculation) {
        const uniqueRecords = [];
        const seenKeys = new Set();
        for (const rec of action.payload.rowTableData) {
          const key = rec.ballotRecordId ? rec.ballotRecordId : rec.id;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueRecords.push(rec);
          }
        }
        state.selectedTableRecords = uniqueRecords;
      }
    },
    addRecordTableFilterData: (state, action: PayloadAction<Record<string, any>>) => {
      state.recordTableFilterData = action.payload.rowTableData;
    },
    postBallotRecord: (state, action: PayloadAction<Record<string, any>>) => {
      state.postBallotRecord = action.payload.value;
    },
    addStepCountHandler: (state, action: PayloadAction<Record<string, any>>) => {
      state.stepCount = action.payload.value;
    },
    isEditHandler: (state, action: PayloadAction<Record<string, any>>) => {
      state.isEdit = action.payload.value;
    },
    countChange: (state, action: PayloadAction<Record<string, any>>) => {
      state.countChange = action.payload.value;
    },
    recordCoutHandler: (state, action: PayloadAction<Record<string, any>>) => {
      state.recordCount = state.recordCount + action.payload.value;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    reset: (state) => {
      state.selectedRecordValues = {
        accreditationBodySelect: [],
        primaryCommitteSelect: [],
        ballotTypeSelect: [],
        ballotSubTypeSelect: [],
        ballotIdSelect: [],
        ballotLevelSelect: [],
        recordTypeSelect: [],
        recordSubTypeSelect: [],
        positionAppliedSelect: [],
      }
      state.selectedTableRecords = [];
      state.preservedSelectedTableRecords = [];
      state.recordTableData = [];
      state.postBallotRecord = false;
      state.stepCount = 0;
      state.isEdit = false;
      state.countChange = false;
      state.recordTableFilterData = [];
      state.primaryCommitteOptions = [];
      state.recordCount = 0;
    },
  }
});

export const ballotSelectRecords = selectRecords.actions;
export default selectRecords.reducer;